// Normalize LLM-authored markdown so `remark-math` reliably parses display
// math. The models (Sage, lesson/explanation generators) very often emit a
// multi-line `$$ … $$` block with the fences HUGGING the content, e.g.
//
//   $$\begin{array}{cccc}
//   a & b \\ c & d
//   \end{array}$$
//
// micromark-extension-math (behind remark-math v6) does NOT recognize a
// multi-line `$$` block when the opening/closing fences share a line with the
// math. It leaks the raw `$$`, then mis-pairs the remaining `$…$` inline spans
// downstream — which surfaces as a big red KaTeX-error blob swallowing every
// heading and paragraph after it (the "Sage response is not formatted" bug).
//
// A `$$` block whose fences sit on their OWN lines parses fine, so we simply
// rewrite every display-math block into that shape. Display math is block-level
// in KaTeX regardless, so promoting an inline-written `$$…$$` to its own lines
// is semantically identical — just parseable.
//
// Code is left untouched: fenced ``` blocks and inline `code` spans are carved
// out first so a `$$` written inside a code sample is never rewritten.

/** Carve out fenced code blocks and inline code so we never rewrite `$$`
 *  that lives inside a code sample. Returns alternating text/code segments. */
function splitCode(src: string): { code: boolean; text: string }[] {
  const segments: { code: boolean; text: string }[] = []
  // Fenced ```…``` blocks (greedy-safe, non-greedy body) OR inline `…` spans.
  const codeRe = /```[\s\S]*?```|`[^`\n]*`/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = codeRe.exec(src)) !== null) {
    if (m.index > last) segments.push({ code: false, text: src.slice(last, m.index) })
    segments.push({ code: true, text: m[0] })
    last = m.index + m[0].length
  }
  if (last < src.length) segments.push({ code: false, text: src.slice(last) })
  return segments
}

/** Rewrite each `$$ … $$` display block in a (code-free) chunk so its fences
 *  sit on their own lines, padded by blank lines. Unbalanced trailing `$$`
 *  (odd count) is left exactly as-is rather than guessed at. */
function fixDisplayBlocks(text: string): string {
  let out = ''
  let i = 0
  while (i < text.length) {
    const open = text.indexOf('$$', i)
    if (open === -1) {
      out += text.slice(i)
      break
    }
    const close = text.indexOf('$$', open + 2)
    if (close === -1) {
      // Unbalanced — don't fabricate a closing fence; leave the rest verbatim.
      out += text.slice(i)
      break
    }
    out += text.slice(i, open)
    const inner = text.slice(open + 2, close).trim()
    // Empty `$$$$` is meaningless math — drop the fences rather than emit a
    // KaTeX error for a blank block.
    out += inner.length === 0 ? '' : `\n\n$$\n${inner}\n$$\n\n`
    i = close + 2
  }
  return out
}

/**
 * Make LLM math markdown safe for remark-math: put every `$$` display fence on
 * its own line. Inline `$…$` spans, currency, and code are left untouched.
 */
export function normalizeMathMarkdown(src: string): string {
  if (!src.includes('$$')) return src // fast path: no display math to fix
  return splitCode(src)
    .map((seg) => (seg.code ? seg.text : fixDisplayBlocks(seg.text)))
    .join('')
}
